import { CSSProperties, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { validatePAN } from '../../../../../utils/validatePAN';
import { emailRegex, validateDIN, validateTenDigitPhone } from '../../../../../utils/utils';
import { createDirector } from '../../../../../store/dashboard/overview/overview_apiHelper';
import { AddDirectorFormState, AddDirectorModalProps, FormErrors } from './types';
import { EMPTY_FORM } from './constants';

// PAN malformation checks are ordered so each state is reachable: too-short catches anything
// under 10 chars first, invalid-chars catches non-alphanumeric input at length 10+, and the
// generic message is the fallback for a 10-char alphanumeric string in the wrong layout
// (including one that is too long but otherwise alphanumeric).
const getPanError = (rawValue: string): string | undefined => {
    if (!rawValue) return undefined;
    const value = rawValue.trim().toUpperCase();
    if (value.length < 10) return 'PAN must be 10 characters — 5 letters, 4 digits, and 1 letter.';
    if (!/^[A-Z0-9]+$/.test(value)) return 'PAN can only contain letters and numbers in the format ABCDE1234F.';
    if (!validatePAN(value)) return 'Please enter a valid PAN (e.g., ABCDE1234F).';
    return undefined;
};

// Same reachability reasoning as PAN: too-short first, then non-numeric, then the generic
// message as the fallback (e.g. a too-long numeric string).
const getDinError = (rawValue: string): string | undefined => {
    if (!rawValue) return undefined;
    const value = rawValue.trim();
    if (value.length < 8) return 'DIN must be exactly 8 digits.';
    if (!/^[0-9]+$/.test(value)) return 'DIN can only contain numbers.';
    if (!validateDIN(value)) return 'Please enter a valid 8-digit DIN.';
    return undefined;
};

export default function useAddDirectorModalLogic({ open, onClose, onSuccess, organizationId }: AddDirectorModalProps) {
    const [form, setForm] = useState<AddDirectorFormState>(EMPTY_FORM);
    const [errors, setErrors] = useState<FormErrors>({});
    const [saving, setSaving] = useState(false);
    // Which of the 5 floating-label fields (if any) currently has native focus — used only to
    // pick the right labelStyle override below, never fed into validation/payload logic.
    const [focusedField, setFocusedField] = useState<keyof AddDirectorFormState | null>(null);
    // Purely cosmetic — the create-director request never carried a country code (phoneNumber
    // is sent alone), so the selection here doesn't feed the payload. It only drives the
    // "+91 (IND)" display per the design.
    const [, setPhoneCountryCode] = useState<any>(null);

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setErrors({});
    };

    // D6 — reset on close (covers both an explicit Cancel/X and the parent closing the modal
    // after a successful create) so a reopen never shows the previous attempt's values or errors.
    useEffect(() => {
        if (!open) resetForm();
    }, [open]);

    const setField = (field: keyof AddDirectorFormState, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const buildPayloadOrShowErrors = () => {
        const trimmedName = form.name.trim();
        const trimmedPan = form.pan.trim().toUpperCase();
        const trimmedDin = form.din.trim();
        const trimmedPhone = form.phone.trim();
        const trimmedEmail = form.email.trim();

        const nextErrors: FormErrors = {};
        if (!trimmedName) nextErrors.name = 'Name is required.';

        const panError = getPanError(trimmedPan);
        if (panError) nextErrors.pan = panError;
        const dinError = getDinError(trimmedDin);
        if (dinError) nextErrors.din = dinError;
        if (!trimmedPan && !trimmedDin) nextErrors.panOrDin = 'Enter a PAN or DIN — at least one is required.';

        if (trimmedPhone && !validateTenDigitPhone(trimmedPhone)) {
            nextErrors.phone = 'Please enter a valid 10-digit mobile number.';
        }
        if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
            nextErrors.email = 'Please enter a valid email address.';
        }

        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return null;

        return {
            name: trimmedName,
            primaryPan: trimmedPan || null,
            din: trimmedDin || null,
            designation: form.designation || null,
            phoneNumber: trimmedPhone || null,
            email: trimmedEmail || null,
        };
    };

    const handleSave = async () => {
        if (saving) return;
        if (!organizationId) {
            toast.error('Could not add director. Please try again.');
            return;
        }
        const payload = buildPayloadOrShowErrors();
        if (!payload) return;

        setSaving(true);
        try {
            const result = await createDirector(payload, organizationId);
            if (result.ok) {
                resetForm();
                onSuccess();
                return;
            }
            toast.error(result.message || 'Could not add director. Please try again.');
        } catch {
            // Never log the caught error — it can carry the request payload (PAN/DIN/phone/email).
            console.error('createDirector request failed');
            toast.error('Could not add director. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const floatedLabelGap = '0.375rem';
    // Mirrors Input.module.scss's two floated-label treatments exactly (font-size/weight/color) —
    // `:focus ~ label` wins over `.FormLabelInputHasValue` when both apply, so a focused-with-value
    // field is grey/400, and a blurred-with-value field is black/300.
    const focusedLabelStyle: CSSProperties = {
        top: `-${floatedLabelGap}`,
        left: 0,
        transform: 'translateY(-100%)',
        fontSize: '0.625rem',
        fontWeight: 400,
        color: '#8d8d8d',
    };
    const filledLabelStyle: CSSProperties = {
        ...focusedLabelStyle,
        fontWeight: 300,
        color: '#000000',
    };
    // Resting (empty + unfocused) = the placeholder. Pin its colour to #9d9d9d inline so it always
    // wins over Input's own label colours (.FormLabel var(--label-color), .HasData ~ label #8d8d8d,
    // .FormLabelInputHasValue black) — those otherwise leak a darker/black colour into the
    // placeholder after a type-then-clear cycle. Colour only; position stays at Input's resting.
    const restingLabelStyle: CSSProperties = { color: '#9d9d9d' };
    const getLabelStyle = (fieldKey: keyof AddDirectorFormState, value: string): CSSProperties => {
        if (focusedField === fieldKey) return focusedLabelStyle;
        if (value.trim()) return filledLabelStyle;
        return restingLabelStyle;
    };
    const fieldFocusHandlers = (fieldKey: keyof AddDirectorFormState) => ({
        onFocusProp: () => setFocusedField(fieldKey),
        onBlurProp: () => setFocusedField((current) => (current === fieldKey ? null : current)),
    });

    return {
        form,
        errors,
        saving,
        setField,
        handleClose,
        handleSave,
        getLabelStyle,
        fieldFocusHandlers,
        setPhoneCountryCode,
    };
}
